/**
@format
@flow local-strict
*/

import React from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import Colors from '@ziynx/theme/colors';
import { Heading, Body } from '@ziynx/components/typography/';
import { withAppManager } from '@ziynx/components/utilities';
import Icon from '@ziynx/components/icons';
import Image from '@ziynx/components/images';
import { Constants } from '@ziynx/components/layout';
import Link from '@ziynx/components/links';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import type { ViewportType } from '@ziynx/flow-types/viewports';
import MenuButton from './MenuButton';
import ziynxLogo from '../../assets/ZIYNX_LOGO_2.png';

type Props = $ReadOnly<{|

   /**
   * Determines which nav bar to render.
   */
  variant?: 'loggedIn' | 'loggedOut',

  /**
   * Link to previous page
   * Format {previousPageName, currentPage, path}
   */
  backLink?: { previousPageName: string, currentPage: string, path: string },

  /**
   * Offset of spacing on the left (set to width of sidebar).
   * Default 0 for loggedOut variant
   */
  offsetLeft?: number,

  /**
   * Content passed
   */
  children?: any,
|}>;

type SubVariant = $PropertyType<Props, 'variant'> | 'hasBackLink';

function calculatePaddingLeft(
  offsetLeft: number,
  variant: $NonMaybeType<SubVariant>,
  sideNavExpanded: boolean,
) {
  if ((variant === 'loggedIn' || variant === 'hasBackLink') && !sideNavExpanded) {
    return `padding-left: calc(${offsetLeft}px + 40px); transition: padding-left 400ms`;
  }
  if ((variant === 'loggedIn' || variant === 'hasBackLink') && sideNavExpanded) {
    return `padding-left: calc(${offsetLeft}px + 268px); transition: padding-left 400ms`;
  }
  return 'padding-left: 80px';
}

const InputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 46px;
 ${({ offsetLeft, variant, sideNavExpanded }) => calculatePaddingLeft(offsetLeft, variant, sideNavExpanded)};
`;

const SearchInputWrapper = styled.div`
  ${({ variant }) => variant !== 'hasBackLink' && 'margin-left: 12px;'};
  display: flex;
  align-items: center;
`;

const LogoWrapper = styled.div`
`;

function calculateNavBarPadding(viewport: ViewportType) {
  let padding;
  switch (viewport) {
  case 'desktop':
  case 'desktopSmall':
  case 'tablet':
    padding = '0px 40px';
    break;
  case 'mobile':
  case 'mobileSmall':
  default:
    padding = '0px 16px';
    break;
  }
  return padding;
}

// Currently using carret-down icon and rotate 90deg to achieve a carret-left icon
const IconWrapper = styled.div`
  ${({ variant }) => variant === 'hasBackLink' && 'transform: rotate(90deg);'};
  align-items: center;
`;

const StyledHeader = styled.div`
  position: ${({ viewport }) => (viewport === 'desktop' ? 'absolute' : 'fixed')}; 
  top: 0;
  left: 0;
  right: 0;
  overflow: hidden;
  box-shadow: ${Constants.boxShadowTotal};
  padding: ${({ viewport }) => calculateNavBarPadding(viewport)};
  height: ${({ viewport }) => (viewport === 'mobileSmall' || viewport === 'mobile' ? '84px' : '94px')}; 
  justify-content: space-between;
  text-align: left;
  display: flex;
  align-items: center;
  width: -webkit-fill-available;
  z-index: 985;
  background-color: ${({ variant, isDesktop }) => (variant === 'loggedOut' || !isDesktop ? Colors.White : Colors.GrayFaded)};
`;

function renderBell(variant) {
  if (variant === 'loggedIn') {
    return (
      <IconWrapper>
        <Icon name="notification-bell" veryTiny />
      </IconWrapper>
    );
  }
  return null;
}

const NavBar = (props: ManagedProps<Props>): React$Node => {
  const {
    children,
    variant,
    offsetLeft,
    backLink,
    sideNavExpanded,
    viewport,
  } = props;
  const location = useLocation();

  const { previousPageName, currentPage, path } = backLink || {};

  const isDesktop = viewport === 'desktop';

  let subVariant: SubVariant;
  /**
     * subVariant keeps track if there's a previousPageName or not
     * to adjust in the styled components
     */
  if (location?.pathname === currentPage) {
    subVariant = 'hasBackLink';
  } else {
    subVariant = variant;
  }

  const showNav = location?.pathname !== '/profile-setup';

  /**
   * Check current location against currentPage of backLink object
   * Example: if location /change-password matches the currentPage,
   * will signal to render backLink.
   */
  const renderHeaderContent = (type: string = ''): React$Node => {
    let Component;

    switch (type) {
    case 'loggedIn':
      Component = (
        <>
          {location?.pathname !== currentPage && false && (
            <>
              <Icon name="search" veryTiny />
              <SearchInputWrapper variant={subVariant}>
                <Body medium color={Colors.Gray}>Search</Body>
              </SearchInputWrapper>
            </>
          )}

          {location?.pathname === currentPage && (
            <SearchInputWrapper variant={subVariant}>
              <Link variant="navigation" activeOnlyWhenExact to={path} color={Colors.GrayDark}>
                <IconWrapper variant={subVariant}>
                  <Icon name="carret-down" tiny color={Colors.GrayDark} />
                </IconWrapper>
                &nbsp;Back to&nbsp;
                {previousPageName}
              </Link>
            </SearchInputWrapper>
          )}
        </>
      );
      break;

    case 'loggedOut':
    default:
      Component = (
        <Heading large primitive="h1" color={Colors.Black}>
          {children}
        </Heading>
      );
      break;
    }
    return Component;
  };
  return (
    showNav ? (
      <StyledHeader viewport={viewport} isDesktop={isDesktop} variant={variant}>
        {!isDesktop && (
          <>
            <MenuButton />
            <LogoWrapper>
              <Image width="96px" height="20px" src={ziynxLogo} />
            </LogoWrapper>
          </>
        )}
        {isDesktop
          && (
            <>
              <InputWrapper
                offsetLeft={offsetLeft}
                sideNavExpanded={sideNavExpanded}
                variant={variant}
              >
                {renderHeaderContent(variant)}
              </InputWrapper>
            </>
          ) }
        {false && renderBell(subVariant)}
      </StyledHeader>
    ) : null
  );
};

NavBar.defaultProps = {
  children: 'ZIYNX',
  offsetLeft: 0,
  variant: 'loggedOut',

};

export default (withAppManager(NavBar): React$ComponentType<
  React$Config<
    Props,
    typeof NavBar.defaultProps>
>);
